/* istanbul ignore file */

import {
  createEventCallbackRedirect,
  EVENT_TIMEOUT_IN_MS,
  sendGAEvent,
} from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';

import { CART_PATH } from 'src/constants/page';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getSectionVisibleActionType } from 'src/redux/visibilityTracking';
import { formatIntegerPriceToDecimal } from 'src/utils/price';
import { MAIN_PAGE_CTA_ID } from '../constants';
import { actions } from '../modules/slice';
import {
  BuyGiftCard,
  CartModalContinueShopping,
  CartModalGoToBasket,
  CartUpdateModalEvent,
  DELIVERY_SUMMARY_VISIBILITY_NAME,
  DeliverySummaryImpression,
  InitialOpenCartModalEvent,
  MAIN_PAGE_CTA_VISIBILITY_NAME,
  MainPageCTAImpression,
} from './events';

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [getSectionVisibleActionType(DELIVERY_SUMMARY_VISIBILITY_NAME)]: () => {
    sendGAEvent<DeliverySummaryImpression>({
      event: 'custom_event',
      event_category: 'Delivery summary',
      event_action: 'Impression',
      event_label: 'not set',
    });
  },
  [getSectionVisibleActionType(MAIN_PAGE_CTA_VISIBILITY_NAME)]: () => {
    const ctaButton: HTMLElement | null = document.getElementById(MAIN_PAGE_CTA_ID);

    sendGAEvent<MainPageCTAImpression>({
      event: 'custom_event',
      event_category: 'Product page CTA',
      event_action: 'Impression',
      event_label: ctaButton?.textContent ?? '',
    });
  },
  [actions.openCartModal.type]: (state) => {
    const {
      basket: { trackingData },
    } = state;

    if (trackingData) {
      sendGAEvent<InitialOpenCartModalEvent>({
        ecommerce: null,
      });

      sendGAEvent<CartUpdateModalEvent>({
        event: 'cart_update',
        eventCategory: 'Product page',
        eventAction: 'Basket state',
        eventLabel: 'Basket contents',
        ecommerce: {
          checkout: {
            basket_id: trackingData.analyticsId,
            total_items_GBP: formatIntegerPriceToDecimal(trackingData.discountedItemsTotal.cents),
            products: trackingData.items.map(({ purchasePrice, product, quantity }) => ({
              product_code: String(product.code),
              quantity: String(quantity),
              price_GBP: formatIntegerPriceToDecimal(purchasePrice.cents / quantity),
            })),
          },
        },
      });
    }
  },
  [actions.onCartContinueShopping.type]: () => {
    sendGAEvent<CartModalContinueShopping>({
      event: 'custom_event',
      event_action: 'Continue shopping click',
      event_category: 'Added to basket modal',
      event_label: 'not set',
    });
  },
  [actions.onCartGoToBasket.type]: () => {
    sendGAEvent<CartModalGoToBasket>({
      event: 'custom_event',
      event_action: 'Basket click',
      event_category: 'Added to basket modal',
      event_label: 'not set',
      eventCallback: createEventCallbackRedirect(CART_PATH),
      eventTimeout: EVENT_TIMEOUT_IN_MS,
    });
  },
  [actions.onBuyGiftCard.type]: () => {
    sendGAEvent<BuyGiftCard>({
      event: 'custom_event',
      event_action: 'Buy giftcard',
      event_category: 'Product page',
      event_label: 'not set',
    });
  },
};
